/**
 * Tagada Rx — store dashboard (active integrations only).
 *
 * One glance answers: how much am I selling, where are my cases stuck,
 * what does each treatment cost me, how fast do doctors and pharmacies
 * move. Data comes from `rx.dashboard` (single aggregate query set).
 */

'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ArrowDownRight, ArrowUpRight, Clock, Loader2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { formatCents, useRxDashboard, type RxStoreDashboard } from '@/lib/api/rx';
import { cn } from '@/lib/utils';

const STATUS_KEY: Record<string, string> = {
  requested: 'cases.status.requested',
  submitted: 'cases.status.submitted',
  in_review: 'cases.status.inReview',
  approved: 'cases.status.approved',
  shipped: 'cases.status.shipped',
};

function Delta({ current, previous }: { current: number; previous: number }) {
  if (previous === 0) return null;
  const pct = ((current - previous) / previous) * 100;
  if (!Number.isFinite(pct) || Math.abs(pct) < 0.5) return null;
  const up = pct > 0;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 text-xs font-medium',
        up ? 'text-emerald-600' : 'text-red-600',
      )}
    >
      {up ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
      {Math.abs(pct).toFixed(0)}%
    </span>
  );
}

function StatTile({
  label,
  value,
  sub,
  delta,
}: {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  delta?: React.ReactNode;
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
          {label}
        </div>
        <div className="mt-1 flex items-baseline gap-2">
          <div className="text-2xl font-bold">{value}</div>
          {delta}
        </div>
        {sub ? <div className="text-muted-foreground mt-0.5 text-xs">{sub}</div> : null}
      </CardContent>
    </Card>
  );
}

function NotConfigured() {
  const { t } = useTranslation('rx');
  return (
    <Badge variant="outline" className="text-muted-foreground text-[10px] uppercase">
      {t('dashboard.notConfigured')}
    </Badge>
  );
}

function CostsCard({ data }: { data: RxStoreDashboard }) {
  const { t } = useTranslation('rx');
  const { costs, currency, feeSchedule } = data;
  const initialFee = feeSchedule?.initialVisitMinor ?? null;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('dashboard.costs.title')}</CardTitle>
        <CardDescription>{t('dashboard.costs.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="flex items-center justify-between">
          <div>
            <div>{t('dashboard.costs.initial')}</div>
            <div className="text-muted-foreground text-xs">
              {t('dashboard.costs.initialSub', { count: costs.consultInitialCount })}
              {initialFee != null
                ? t('dashboard.costs.initialWithFee', { amount: formatCents(initialFee, currency) })
                : ''}
            </div>
          </div>
          <div className="font-semibold">
            {initialFee != null ? (
              formatCents(costs.consultInitialCount * initialFee, currency)
            ) : (
              <NotConfigured />
            )}
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <div>{t('dashboard.costs.followUp')}</div>
            <div className="text-muted-foreground text-xs">
              {t('dashboard.costs.followUpSub', { count: costs.consultFollowUpCount })}
            </div>
          </div>
          <div className="font-semibold">
            {costs.consultCostMinor != null && initialFee != null ? (
              formatCents(
                costs.consultCostMinor - costs.consultInitialCount * initialFee,
                currency,
              )
            ) : (
              <NotConfigured />
            )}
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div>{t('dashboard.costs.medication')}</div>
          <div className="font-semibold">
            {costs.medicationCostMinor != null ? (
              formatCents(costs.medicationCostMinor, currency)
            ) : (
              <NotConfigured />
            )}
          </div>
        </div>
        <div className="flex items-center justify-between border-t pt-3">
          <div className="font-medium">{t('dashboard.costs.margin')}</div>
          <div className="text-lg font-bold text-emerald-700">
            {costs.complete && costs.consultCostMinor != null && costs.medicationCostMinor != null
              ? formatCents(
                  data.period.revenueMinor - costs.consultCostMinor - costs.medicationCostMinor,
                  currency,
                )
              : '—'}
          </div>
        </div>
        {!costs.complete ? (
          <p className="text-muted-foreground text-xs">{t('dashboard.costs.incomplete')}</p>
        ) : null}
      </CardContent>
    </Card>
  );
}

export function RxDashboard({ storeId }: { storeId: string }) {
  const { t } = useTranslation('rx');
  const [days, setDays] = React.useState(30);
  const query = useRxDashboard(storeId, days);

  if (query.isLoading || !query.data) {
    return (
      <Card>
        <CardContent className="flex items-center gap-2 p-6 text-sm">
          <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
          {t('dashboard.loading')}
        </CardContent>
      </Card>
    );
  }

  const data = query.data;
  const { period, previousPeriod, currency } = data;
  const hasAnyCase = data.pipeline.length > 0 || period.newCases > 0 || data.activeTreatments > 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end">
        <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
          <SelectTrigger className="h-8 w-36 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="30">{t('dashboard.range.days30')}</SelectItem>
            <SelectItem value="60">{t('dashboard.range.days60')}</SelectItem>
            <SelectItem value="90">{t('dashboard.range.days90')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile
          label={t('dashboard.tiles.revenue')}
          value={formatCents(period.revenueMinor, currency)}
          delta={<Delta current={period.revenueMinor} previous={previousPeriod.revenueMinor} />}
          sub={t('dashboard.tiles.revenueSub', {
            amount: formatCents(previousPeriod.revenueMinor, currency),
          })}
        />
        <StatTile
          label={t('dashboard.tiles.activeTreatments')}
          value={data.activeTreatments}
          sub={t('dashboard.tiles.activeSub', { count: data.scheduledRebills })}
        />
        <StatTile
          label={t('dashboard.tiles.newTreatments')}
          value={period.newTreatments}
          delta={<Delta current={period.newTreatments} previous={previousPeriod.newTreatments} />}
          sub={t('dashboard.tiles.newSub', { count: period.rebills })}
        />
        <StatTile
          label={t('dashboard.tiles.endedTreatments')}
          value={period.endedTreatments}
          sub={t('dashboard.tiles.endedSub')}
        />
      </div>

      {!hasAnyCase ? (
        <Card>
          <CardContent className="text-muted-foreground p-6 text-sm">
            {t('dashboard.empty')}
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-base">{t('dashboard.charts.revenueTitle')}</CardTitle>
              </CardHeader>
              <CardContent className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data.series} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="rxRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#059669" stopOpacity={0.25} />
                        <stop offset="95%" stopColor="#059669" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                    <XAxis
                      dataKey="day"
                      tick={{ fontSize: 11 }}
                      tickFormatter={(d: string) => d.slice(5)}
                      minTickGap={24}
                    />
                    <YAxis
                      tick={{ fontSize: 11 }}
                      tickFormatter={(v: number) => `$${Math.round(v / 100)}`}
                      width={48}
                    />
                    <Tooltip
                      formatter={(value: number | string, name: string) =>
                        name === 'revenueMinor'
                          ? [formatCents(Number(value), currency), t('dashboard.tooltip.revenue')]
                          : [value, t('dashboard.tooltip.cases')]
                      }
                    />
                    <Area
                      type="monotone"
                      dataKey="revenueMinor"
                      stroke="#059669"
                      strokeWidth={2}
                      fill="url(#rxRevenue)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('dashboard.charts.pipelineTitle')}</CardTitle>
                <CardDescription>{t('dashboard.charts.pipelineCaption')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {data.pipeline.length === 0 ? (
                  <p className="text-muted-foreground text-sm">{t('dashboard.charts.pipelineEmpty')}</p>
                ) : (
                  data.pipeline.map((row) => (
                    <div key={row.status} className="flex items-center justify-between text-sm">
                      <span>{STATUS_KEY[row.status] ? t(STATUS_KEY[row.status]) : row.status}</span>
                      <Badge variant="secondary" className="font-mono">
                        {row.count}
                      </Badge>
                    </div>
                  ))
                )}
                <div className="text-muted-foreground flex items-center gap-1.5 border-t pt-3 text-xs">
                  <Clock className="h-3.5 w-3.5" />
                  {data.turnaround.submitToApproveHours != null
                    ? t('dashboard.turnaround.review', { h: data.turnaround.submitToApproveHours })
                    : t('dashboard.turnaround.noSample')}
                  {data.turnaround.approveToShipHours != null
                    ? t('dashboard.turnaround.reviewAndShip', { h: data.turnaround.approveToShipHours })
                    : ''}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('dashboard.charts.salesTitle')}</CardTitle>
                <CardDescription>{t('dashboard.charts.salesCaption')}</CardDescription>
              </CardHeader>
              <CardContent>
                {data.salesByTreatment.length === 0 ? (
                  <p className="text-muted-foreground text-sm">{t('dashboard.charts.salesEmpty')}</p>
                ) : (
                  <div className="space-y-2">
                    {data.salesByTreatment.map((row) => {
                      const max = data.salesByTreatment[0]?.revenueMinor || 1;
                      return (
                        <div key={row.name}>
                          <div className="flex items-center justify-between text-sm">
                            <span className="truncate pr-2">{row.name}</span>
                            <span className="whitespace-nowrap font-medium">
                              {formatCents(row.revenueMinor, currency)}
                            </span>
                          </div>
                          <div className="bg-muted mt-1 h-1.5 overflow-hidden rounded-full">
                            <div
                              className="h-full rounded-full bg-emerald-600"
                              style={{ width: `${Math.max(4, (row.revenueMinor / max) * 100)}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            <CostsCard data={data} />
          </div>
        </>
      )}
    </div>
  );
}
